import { isFunction } from '#lib/utils/function.js';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

export function useManageDate({
    startDate,
    endDate,
    days,
    plans,
    addDay,
    deleteDay,
    movePlan,
    editPlan,
    form,
    headerError,
    clearError
}) {
    const [outdatedEndDate, setOutdatedEndDate] = useState(endDate);
    const [outdatedStartDate, setOutdatedStartDate] = useState(startDate);
    const maxDaysWarning = 30;
    const maxDays = 180;

    // Function to add days to a date
    function addDaysToDate(date, days) {
        const result = new Date(date); // Create a new Date object to avoid mutating the original
        result.setDate(result.getDate() + days); // Add the number of days
        return result.toISOString().split('T')[0];
    }

    const handleChangeStartDate = () => {
        const value = form.data.startDate;
        // const value = e.target.value;
        if (value && headerError?.some((el) => el === 'startDate') && isFunction(clearError)) clearError('startDate');

        // check max days of itinerary
        const totalDays = (new Date(endDate) - new Date(outdatedStartDate)) / (1000 * 60 * 60 * 24);
        const diff = (new Date(value) - new Date(outdatedStartDate)) / (1000 * 60 * 60 * 24);
        if (diff === 0) return;

        // days will be added only if the new date is before the old one
        if (diff < 0) {
            const daysToAdd = Math.abs(diff);
            // check days limit exceeded
            if (totalDays + daysToAdd > maxDays) {
                toast.error(_t.edit.maxDaysError(maxDays));
                form.handleChange('startDate', outdatedStartDate); // reset old date
                return;
            }
            // prompt for confirmation if it exceeds the maxDaysWarning
            if (daysToAdd > maxDaysWarning && !confirm(_t.edit.maxDaysWarning(daysToAdd))) {
                form.handleChange('startDate', outdatedStartDate);
                return;
            }

            toast.success(_t.edit.addedNDays(daysToAdd));
            for (let i = 0; i < daysToAdd; i++) addDay(0);

            if (confirm(_t.edit.movePlansWithDate())) {
                plans.forEach((el) => {
                    movePlan([el.dayIndex + daysToAdd, el.planIndex], [el.dayIndex, el.planIndex]);

                });
            }
        }

        if (diff > 0) {
            const daysToRemove = Math.abs(diff);

            // check if there are aviability flights in the days to be unlinked from aviability service
            const aviabilityFlights = plans.filter((el) => el.flightKey);
            if (aviabilityFlights.length) {
                aviabilityFlights.forEach((el) => {
                    const dayIndex = el.dayIndex;
                    editPlan([`day-${dayIndex}`, el.id], 'flightKey', '');
                    toast(_t.edit.planner.flightUnlinked(el.name));
                });
            }
            // update endDate and add days at the end
            const newEndDate = addDaysToDate(endDate, daysToRemove);
            form.handleChange('endDate', newEndDate);
        }

        form.handleChange('startDate', value);
    };

    const handleChangeEndDate = () => {
        const value = form.data.endDate;
        if (value === outdatedEndDate) return;
        const duration = (new Date(value) - new Date(startDate)) / (1000 * 60 * 60 * 24);
        if (duration < 0) {
            toast.error(_t.edit.endDateError());
            form.handleChange('endDate', outdatedEndDate);
            return;
        }
        // check max days of itinerary
        if (duration > maxDays) {
            toast.error(_t.edit.maxDaysError(maxDays));
            form.handleChange('endDate', outdatedEndDate);
            return;
        }
        // Check if the new duration is less than the checkout date of the accommodations
        const accommodations = plans.filter((el) => el.type === 'accommodation');
        accommodations.forEach((el) => {
            const checkout = el.dayIndex + el.nights;
            const diff = checkout - duration;
            if (diff > 0) {
                const newNights = el.nights - diff > 0 ? el.nights - diff : 1;
                // Spostare il plan al giorno con idx = duration
                editPlan([`day-${el.dayIndex}`, el.id], 'nights', newNights);
            }
        });
        // Move all the plans that are after the new duration
        plans.forEach((el) => {
            if (el.dayIndex > duration) {
                movePlan([el.dayIndex, 0], [duration, days[duration].plans.length]);
            }
        });
        // remove days
        days.forEach((el, idx) => {
            if (idx > duration) deleteDay(el.id);
        });
        // add days
        const diff = duration - days.length + 1;
        if (diff > 0) {
            if (diff > maxDaysWarning && !confirm(_t.edit.maxDaysWarning(diff))) {
                form.handleChange('endDate', outdatedEndDate);
                return;
            }
            for (let i = 0; i < diff; i++) addDay();
            // toast.success(_t.edit.addedNDays(Math.abs(diff)));
        }
        // if (diff < 0) toast.success(_t.edit.removedNDays(Math.abs(diff)));
        if (value) form.handleChange('endDate', value);
    };

    useEffect(() => {
        // calc end date

        if (days.length) {
            const newEndDate = addDaysToDate(startDate, days.length - 1);
            if (newEndDate === endDate) return;
            form.handleChange('endDate', newEndDate);
        }
    }, [days]);

    return {
        handleChangeStartDate,
        handleChangeEndDate,
        setOutdatedEndDate,
        setOutdatedStartDate
    };
}
